import React from 'react';
import { useForm } from 'react-hook-form';

import TextArea from '@components/form/TextArea/TextArea';
import Button from '@components/uikit/Button/Button';
import Input from '@components/uikit/Input/Input';

import { showToast } from '@helpers/showToast';

import useTranslation from '@hooks/useTranslation';

import { useUserProfileQuery } from '@services/user/useUserProfileQuery';

import { useModalActions } from '@store/useModalStore';

import AttachmentsField from '../CompletionReportModal/components/AttachmentsField/AttachmentsField';
import ModalHeader from '../ModalHeader/ModalHeader';
import {
  CONTACT_SUPPORT_FIELDS,
  type IContactSupportForm,
  getContactSupportDefaultValues,
  schemaContactSupport,
} from './constants/schemaContactSupport';
import { useContactSupportFormMutation } from './services/useContactSupportFormMutation';
import style from './style.module.scss';

const ContactSupportModal = () => {
  const { t } = useTranslation();

  const { setModalClose } = useModalActions();

  const { data: profileData } = useUserProfileQuery();

  const { control, handleSubmit, reset } = useForm<IContactSupportForm>({
    mode: 'onChange',
    resolver: schemaContactSupport,
    defaultValues: getContactSupportDefaultValues(profileData?.user),
  });

  const handleClose = () => {
    setModalClose();
    reset();
  };

  const { mutate: contactSupportFormMutate, isLoading: contactSupportFormLoading } =
    useContactSupportFormMutation();

  const submit = handleSubmit((data) => {
    const formData = {
      user_name: data.fullName,
      email: data.email,
      text: data.description,
      attachments: data.attachments,
    };

    contactSupportFormMutate(formData, {
      onSuccess: () => {
        showToast({ variant: 'success', message: t('Contact support form has been sent.') });
        handleClose();
      },
      onError: (error) => {
        if (error) {
          showToast({
            variant: 'error',
            message: error?.response?.data?.message || t('Something went wrong! Please, try again later.'),
          });
        }
      },
    });
  });

  const closeModal = () => {
    setModalClose();
    reset();
  };

  return (
    <div className={style.contactSupportModal}>
      <ModalHeader title={'Contact Support Form'} onClose={handleClose} />
      <div className={style.modalBody}>
        <div className={style.personalInformationContainer}>
          <Input control={control} name={CONTACT_SUPPORT_FIELDS.FULL_NAME} label={t('Full Name')} required />
          <Input
            control={control}
            name={CONTACT_SUPPORT_FIELDS.EMAIL_ADDRESS}
            label={t('Email Address')}
            required
          />
        </div>
        <TextArea
          name={CONTACT_SUPPORT_FIELDS.DESCRIPTION}
          control={control}
          label={t('General Description of Problem or Issue')}
          required
        />
        <AttachmentsField name={CONTACT_SUPPORT_FIELDS.ATTACHMENTS} control={control} label="Attachments" />
      </div>
      <div className={style.modalFooter}>
        <Button type="secondary" text={t('Cancel')} onClick={closeModal} />
        <Button type="primary" text={t('Send')} disabled={contactSupportFormLoading} onClick={submit} />
      </div>
    </div>
  );
};

export default ContactSupportModal;
